import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { extractApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const InviteRedeem = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuthSafe();
  const [state, setState] = useState({ status: "loading", error: "", name: "" });
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState({ status: "error", error: "رابط الدعوة غير صالح", name: "" });
      return;
    }
    // Guard against React.StrictMode double-invocation, bf-cache restores, retries.
    if (calledRef.current) return;
    calledRef.current = true;

    api
      .post("/auth/redeem-invite", { token })
      .then(({ data }) => {
        localStorage.setItem("token", data.token);
        if (setUser) setUser(data.user);
        setState({ status: "ok", error: "", name: data.user.name || "" });
        // Slight pause so the user reads the welcome message
        setTimeout(() => navigate("/dashboard", { replace: true }), 1600);
      })
      .catch((e) => {
        // Defensive: if a stale error arrives after we already succeeded, ignore it.
        setState((s) =>
          s.status === "ok"
            ? s
            : { status: "error", error: extractApiErrorMessage(e, "تعذّر فتح رابط الدعوة"), name: "" }
        );
      });
  }, [token, navigate, setUser]);

  return (
    <PageShell>
      <section className="max-w-xl mx-auto px-6 py-20" data-testid="invite-redeem-page">
        <div className="card-warm p-10 text-center">
          {state.status === "loading" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[#0A4D3C] animate-spin" />
              <h1 className="font-heading text-3xl text-[#222222] mt-5">جاري تسجيل الدخول…</h1>
              <p className="text-[#5E5C58] mt-2">لحظات من فضلك حتى نُجهز حسابك.</p>
            </>
          )}
          {state.status === "ok" && (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
              <h1 className="font-heading text-3xl text-[#222222] mt-5" data-testid="invite-success">
                مرحبًا{state.name ? ` ${state.name}` : ""} 🌿
              </h1>
              <p className="text-[#5E5C58] mt-2">
                تم تسجيل دخولك تلقائيًا. سننقلك إلى حسابك خلال لحظات…
              </p>
            </>
          )}
          {state.status === "error" && (
            <>
              <AlertTriangle className="w-12 h-12 mx-auto text-[#B33939]" />
              <h1 className="font-heading text-3xl text-[#222222] mt-5" data-testid="invite-error">
                تعذّر فتح الرابط
              </h1>
              <p className="text-[#5E5C58] mt-2 leading-loose">{state.error}</p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/login">
                  <Button className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white">
                    تسجيل الدخول يدويًا
                  </Button>
                </Link>
                <a
                  href="https://wa.me/966538278484?text=السلام%20عليكم%2C%20أحتاج%20مساعدة%20بخصوص%20رابط%20الدعوة."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="rounded-full">
                    تواصل عبر الواتساب
                  </Button>
                </a>
              </div>
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
};

// Tiny helper so we don't blow up if AuthContext changes shape
function useAuthSafe() {
  try {
    return useAuth();
  } catch {
    return {};
  }
}

export default InviteRedeem;
